import type {
  SacrificeStatsResponse,
  NetworkSacrificeStats,
  TokenSacrificeStats,
  SacrificeInfo,
} from 'sacrifice-utils/types'
import * as keys from '@provex/eip712-utils/keys'
import { NetworkTypes } from '@provex/eip712-utils'

/**
 * Aggregate token-level (step) stats into network-level stats.
 *
 * This is a pure function of the stats response: it filters out tokens whose
 * price source is zero (scam/zero-priced tokens must not inflate a network's
 * USD value or points), sums count / USD value / points per network using
 * BigInt math (guarding against non-numeric values via try/catch), and then
 * sorts the resulting networks by value, count, an optional user-address
 * tiebreaker, priority networks, network type, and finally name.
 *
 * @param data - The sacrifice stats response (supportedTokens / steps / networks).
 * @param userAddresses - Optional list of the viewing user's addresses; used
 *   only as a sort tiebreaker so networks the user has deposited on rank higher.
 * @returns Network-level aggregated stats, sorted for display.
 */
export const aggregateNetworkStats = (
  data: SacrificeStatsResponse,
  userAddresses?: SacrificeInfo[] | null
): NetworkSacrificeStats[] => {
  // Create a Set of token keys that have valid price sources (sourceIsZero === false)
  const validTokenKeys = new Set(
    data.supportedTokens
      .filter(token => token.sourceIsZero !== true)
      .map(token => keys.token(token))
  )

  // Filter steps to only include tokens with valid price sources
  const filteredSteps = data.steps.filter(token => validTokenKeys.has(keys.token(token)))

  // Group tokens by network ID for efficient access
  const tokensByNetwork = new Map<string, TokenSacrificeStats[]>()

  // Initialize empty arrays for each network
  data.networks.forEach(network => {
    tokensByNetwork.set(keys.network(network), [])
  })

  // Group tokens by their network ID (only supported tokens)
  filteredSteps.forEach(token => {
    const networkTokens = tokensByNetwork.get(keys.network(token))
    if (networkTokens) {
      networkTokens.push(token)
    }
  })

  // Calculate stats for each network using the grouped data
  const stat = data.networks.map(network => {
    const networkTokens = tokensByNetwork.get(keys.network(network)) || []

    // Sum counts
    const totalCount = networkTokens.reduce((sum, token) => sum + BigInt(token.count || 0n), 0n)

    // Sum USD values as BigInt for precision
    const totalValue = networkTokens.reduce((sum, token) => {
      const usdValue = token.usdValue
      if (usdValue === null || usdValue === undefined) return sum
      try {
        return sum + BigInt(usdValue)
      } catch {
        console.warn(`Invalid usdValue for token ${keys.token(token)}: ${usdValue}`)
        return sum
      }
    }, 0n)

    // Sum CREDIT points per network from the step data (already joined in backend CTE)
    const totalPoints = networkTokens.reduce((sum, token) => {
      if (token.points === null || token.points === undefined) return sum
      try {
        return sum + BigInt(token.points)
      } catch {
        console.warn(`Invalid points for token ${keys.token(token)}: ${token.points}`)
        return sum
      }
    }, 0n)

    return {
      networkName: network.networkName,
      networkType: network.networkType,
      chainId: network.chainId,
      count: totalCount,
      value: totalValue,
      points: totalPoints,
      rootAddress: network.rootAddress,
    }
  })

  return stat.slice(0).sort((a: NetworkSacrificeStats, b: NetworkSacrificeStats) => {
    const valueA = BigInt(a.value || 0n)
    const valueB = BigInt(b.value || 0n)
    if (valueA !== valueB) return valueB > valueA ? 1 : -1
    if (a.count !== b.count) return b.count > a.count ? 1 : -1

    if (userAddresses) {
      const aUserAddress = userAddresses.find(address => {
        return address.networkType === a.networkType && address.chainId === a.chainId
      })
      const bUserAddress = userAddresses.find(address => {
        return address.networkType === b.networkType && address.chainId === b.chainId
      })

      const aAddrRank = aUserAddress ? 0 : 1
      const bAddrRank = bUserAddress ? 0 : 1
      if (aAddrRank !== bAddrRank) return aAddrRank - bAddrRank
    }

    // Priority networks: Ethereum, Bitcoin, Solana (in that order)
    const getPriorityRank = (network: NetworkSacrificeStats): number => {
      const name = network.networkName.toLowerCase()
      if (name === 'ethereum') return 0
      if (name === 'bitcoin') return 1
      if (name === 'solana') return 2
      return 3
    }

    const aPriorityRank = getPriorityRank(a)
    const bPriorityRank = getPriorityRank(b)
    if (aPriorityRank !== bPriorityRank) return aPriorityRank - bPriorityRank

    const aTypeRank =
      a.networkType === NetworkTypes.EVM || a.networkType === NetworkTypes.TVM ? 0 : 1
    const bTypeRank =
      b.networkType === NetworkTypes.EVM || b.networkType === NetworkTypes.TVM ? 0 : 1
    if (aTypeRank !== bTypeRank) return aTypeRank - bTypeRank

    return a.networkName.localeCompare(b.networkName)
  })
}
